/**
 * ConsultantsPage — iter638 (rewritten as a listing)
 *
 * Преди (iter636) тази страница показваше пълните профили на двамата
 * консултанти един под друг. Сега всеки консултант има самостоятелна
 * страница (/consultants/:slug), а тук показваме само 2 summary карти,
 * които водят към тях.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import { SubpageNav, SubpageBackLink, SubpageFooter } from "@/components/SubpageLayout";
import { listConsultants } from "@/data/consultants";
import { Stethoscope, ArrowRight, ShieldCheck, MapPin, Sparkles, CalendarCheck } from "lucide-react";

const TX = {
  bg: {
    title: "Медицински консултанти",
    desc:
      "Хора, които стоят зад клиничната достоверност на Nutramilo. Всеки модул, всяка препоръка и всеки алгоритъм минават през лекарски поглед — преди да стигнат до теб.",
    back: "Начало",
    eyebrow: "Екипът зад платформата",
    why_title: "Защо това е важно",
    why_p:
      "Nutramilo не е просто AI. Това е дигитален здравен близнак, изграден заедно с практикуващи лекари. Консултантите ни се грижат препоръките да са безопасни, актуални и съобразени с реалната клинична практика — не само с литературата.",
    view_full: "Виж пълния профил",
    cta_title: "Готов да изградиш своя дигитален здравен близнак?",
    cta_sub: "60 секунди безплатна оценка. Без credit card.",
    cta_btn: "Започни сега",
  },
  en: {
    title: "Medical consultants",
    desc:
      "The people behind the clinical credibility of Nutramilo. Every module, every recommendation, every algorithm passes through a physician's eye — before it reaches you.",
    back: "Home",
    eyebrow: "The team behind the platform",
    why_title: "Why this matters",
    why_p:
      "Nutramilo is not just AI. It's a digital health twin built together with practising physicians. Our consultants make sure recommendations are safe, current and aligned with real clinical practice — not only with literature.",
    view_full: "View full profile",
    cta_title: "Ready to build your digital health twin?",
    cta_sub: "60-second free assessment. No credit card.",
    cta_btn: "Get started",
  },
  es: {
    title: "Consultores médicos",
    desc:
      "Las personas detrás de la credibilidad clínica de Nutramilo. Cada módulo, cada recomendación, cada algoritmo pasa por los ojos de un médico — antes de llegar a ti.",
    back: "Inicio",
    eyebrow: "El equipo detrás de la plataforma",
    why_title: "Por qué esto importa",
    why_p:
      "Nutramilo no es solo AI. Es un gemelo digital de salud construido junto con médicos en ejercicio. Nuestros consultores se aseguran de que las recomendaciones sean seguras, actualizadas y alineadas con la práctica clínica real — no solo con la literatura.",
    view_full: "Ver perfil completo",
    cta_title: "¿Listo para construir tu gemelo digital de salud?",
    cta_sub: "Evaluación gratuita en 60 segundos. Sin tarjeta de crédito.",
    cta_btn: "Comenzar",
  },
};

function ConsultantCard({ c, isDark, viewLabel }) {
  const isFuchsia = c.accent === "fuchsia";
  const ringColor = isFuchsia ? "ring-fuchsia-300/40" : "ring-cyan-300/40";
  const borderColor = isDark
    ? (isFuchsia ? "border-fuchsia-500/30" : "border-cyan-500/30")
    : (isFuchsia ? "border-fuchsia-200" : "border-cyan-200");
  const bg = isDark
    ? "bg-[hsl(223,47%,9%)]"
    : (isFuchsia ? "bg-gradient-to-br from-fuchsia-50 to-white" : "bg-white");
  const roleColor = isDark
    ? (isFuchsia ? "text-fuchsia-300" : "text-cyan-300")
    : (isFuchsia ? "text-fuchsia-700" : "text-cyan-700");

  return (
    <Link
      to={`/consultants/${c.slug}`}
      data-testid={`consultant-card-${c.slug}`}
      className={`group block p-6 rounded-2xl border ${borderColor} ${bg} ${isFuchsia ? "" : "shadow-sm"} hover:shadow-xl transition-all`}
    >
      <div className={`w-full aspect-[4/3] rounded-xl overflow-hidden mb-5 ring-2 ${ringColor}`}>
        <img
          src={c.photo}
          alt={c.name}
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          loading="lazy"
        />
      </div>

      <h2 className={`text-2xl font-bold mb-1 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{c.name}</h2>
      <p className={`text-sm mb-1 ${roleColor}`}>{c.short_role || c.role}</p>
      <p className={`text-xs flex items-center gap-1 mb-4 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
        <MapPin className="w-3 h-3" />
        {c.location}
      </p>

      <p className={`text-sm leading-relaxed mb-5 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
        {c.summary}
      </p>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span
          className="inline-flex items-center gap-1.5 text-sm font-bold group-hover:gap-2.5 transition-all"
          style={{ color: isFuchsia ? "var(--brand-pink)" : "rgb(6 182 212)" }}
        >
          {viewLabel}
          <ArrowRight className="w-4 h-4" />
        </span>
        {c.booking_url && (
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full ${isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-700"}`}>
            <CalendarCheck className="w-3 h-3" />
            Online запис
          </span>
        )}
      </div>
    </Link>
  );
}

export default function ConsultantsPage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const t = TX[language === "en" ? "en" : "bg"];
  const consultants = listConsultants(language);

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(223,47%,5%)] text-slate-100" : "bg-gradient-to-b from-white via-fuchsia-50/30 to-white text-slate-900"}`}>
      <SEO title={`${t.title} | Nutramilo`} description={t.desc} path="/consultants" />
      <SubpageNav />

      {/* Hero */}
      <header className={`border-b ${isDark ? "border-[hsl(223,47%,12%)]" : "border-slate-100"}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <SubpageBackLink label={t.back} />
          <div className="mt-6 flex items-center gap-2 text-xs uppercase tracking-widest" style={{ color: "var(--brand-pink)" }}>
            <Stethoscope className="w-4 h-4" />
            <span>{t.eyebrow}</span>
          </div>
          <h1
            className={`mt-3 text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight ${isDark ? "text-white" : "text-[#1A1A2E]"}`}
            style={{ fontFamily: "'Work Sans', sans-serif" }}
            data-testid="consultants-title"
          >
            {t.title}
          </h1>
          <p className={`mt-4 max-w-3xl text-base sm:text-lg leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            {t.desc}
          </p>

          <div
            className={`mt-8 p-5 rounded-2xl border ${isDark ? "bg-[hsl(223,47%,9%)] border-fuchsia-500/30" : "bg-white border-fuchsia-200 shadow-sm"}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-4 h-4" style={{ color: "var(--brand-pink)" }} />
              <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{t.why_title}</h3>
            </div>
            <p className={`text-sm leading-relaxed ${isDark ? "text-slate-300" : "text-slate-700"}`}>{t.why_p}</p>
          </div>
        </div>
      </header>

      {/* Cards grid */}
      <section className={`py-12 sm:py-16 ${isDark ? "bg-[hsl(223,47%,6%)]" : ""}`} data-testid="consultants-grid">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          {consultants.map((c) => (
            <ConsultantCard key={c.slug} c={c} isDark={isDark} viewLabel={t.view_full} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className={`py-16 sm:py-20 ${isDark ? "bg-[hsl(223,47%,5%)]" : "bg-white"}`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <Sparkles className="w-8 h-8 mx-auto mb-4" style={{ color: "var(--brand-pink)" }} />
          <h2 className={`text-3xl sm:text-4xl font-semibold mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
            {t.cta_title}
          </h2>
          <p className={`mb-7 text-base ${isDark ? "text-slate-400" : "text-slate-600"}`}>{t.cta_sub}</p>
          <Link
            to="/register"
            data-testid="consultants-cta-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-white shadow-md hover:shadow-lg transition-shadow"
            style={{ background: "var(--brand-gradient-primary)" }}
          >
            {t.cta_btn}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <SubpageFooter />
    </div>
  );
}
